import Script from "next/script";
import React, { useEffect, useMemo } from "react";

import { LOCALE_TO_ONETRUST_RESOURCE_MAP } from "@/shared/constants";

import { ONETRUST_READY_EVENT_NAME } from "@/utils/onetrust";
import { normalizeSmartBanner, observeSmartBanner } from "@/utils/smartBanner";

import { Locale } from "@/types/locale";

const getAfterpaySdkUrl = (locale: Locale): string => {
    switch (locale) {
        case "en-GB":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_GB;
        case "en-US":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_US;
        case "en-AU":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_AU;
        case "en-NZ":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_NZ;
        case "en-CA":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_CA;
        case "fr-CA":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_FR_CA;
        case "global":
            return process.env.NEXT_PUBLIC_AFTERPAY_MERCHANT_SDK_EN_AU;
    }
};

const AFTERPAY_SDK_SCRIPT_ID_PREFIX = "afterpay-merchant-sdk";

type OwnProps = {
    currentLocale: Locale;
};
type Props = OwnProps;
const SharedScripts: React.FC<Props> = ({ currentLocale }) => {
    const oneTrustData = LOCALE_TO_ONETRUST_RESOURCE_MAP[currentLocale];
    const afterpaySdkUrl = useMemo(() => getAfterpaySdkUrl(currentLocale), [currentLocale]);

    useEffect(() => {
        return observeSmartBanner(normalizeSmartBanner);
    }, []);

    const onetrustCookieClearScript =
        currentLocale === "en-GB" ? (
            <Script
                id="onetrust-cookie-clear"
                type="text/javascript"
                strategy="afterInteractive"
                src={"/scripts/occ.js"}
            />
        ) : null;

    const cookieClearScript =
        currentLocale === "en-GB" ? (
            <Script
                id="onetrust-cookie-clear"
                type="text/javascript"
                strategy="afterInteractive"
                src={"/scripts/necc.js"}
            />
        ) : null;

    const oneTrustSdkScript = oneTrustData?.id && (
        <>
            <Script
                src={oneTrustData.otSDKStubURL}
                type="text/javascript"
                charSet="UTF-8"
                data-domain-script={oneTrustData.id}
                strategy="beforeInteractive"
            ></Script>
            <Script
                type="text/javascript"
                // Inline scripts can't be load with beforeInteractive: https://nextjs.org/docs/basic-features/script
                strategy="afterInteractive"
                id="oneTrust-OptanonWrapper"
            >
                {`function OptanonWrapper() { window.dispatchEvent(new Event("${ONETRUST_READY_EVENT_NAME}")); }`}
            </Script>
        </>
    );

    const oneTrustAutoBlockScript = oneTrustData?.id && (
        <Script
            type="text/javascript"
            src={oneTrustData.otAutoBlockURL}
            strategy="beforeInteractive"
        ></Script>
    );

    return (
        <>
            {cookieClearScript}
            {onetrustCookieClearScript}
            {oneTrustSdkScript}
            <Script id={`${AFTERPAY_SDK_SCRIPT_ID_PREFIX}-${currentLocale}`} src={afterpaySdkUrl} />
            {oneTrustAutoBlockScript}
        </>
    );
};

export default SharedScripts;
